import { useEffect, useState } from "react";
import { Check, Images, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiGet } from "@/api/client";
import { BACKEND_URL } from "@/api/client";

export function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL || ""}${url}`;
}

export function AssetImagePicker({
  mode = "single",
  value,
  onChange,
  testIdPrefix = "asset-picker",
  label = "Choose from Assets",
}) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const selectedUrls = mode === "multi"
    ? (Array.isArray(value) ? value : [])
    : (value ? [value] : []);

  useEffect(() => {
    let active = true;
    apiGet("/assets")
      .then((result) => {
        if (!active) return;
        setAssets((result.items || []).filter((item) => String(item.content_type || "").startsWith("image/")));
      })
      .catch(() => {
        if (active) setAssets([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const toggle = (asset) => {
    const url = asset.url;
    if (mode === "single") {
      onChange(url);
      setOpen(false);
      return;
    }
    const exists = selectedUrls.includes(url);
    onChange(exists ? selectedUrls.filter((item) => item !== url) : [...selectedUrls, url]);
  };

  const clear = () => {
    onChange(mode === "multi" ? [] : "");
  };

  return (
    <div className="rounded-lg border bg-[#F8F5EE] p-3" data-testid={`${testIdPrefix}-wrap`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-extrabold" data-testid={`${testIdPrefix}-label`}>{label}</p>
        <div className="flex gap-2">
          {selectedUrls.length > 0 && (
            <Button type="button" size="sm" variant="outline" className="rounded-lg" data-testid={`${testIdPrefix}-clear-button`} onClick={clear}>
              Clear
            </Button>
          )}
          <Button type="button" size="sm" className="rounded-lg" data-testid={`${testIdPrefix}-toggle-button`} onClick={() => setOpen((current) => !current)}>
            <Images className="h-4 w-4" />
            {open ? "Hide library" : "Pick from Assets"}
          </Button>
        </div>
      </div>

      {selectedUrls.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid={`${testIdPrefix}-selected`}>
          {selectedUrls.map((url) => (
            <div key={url} className="relative overflow-hidden rounded-lg border bg-white">
              <img src={resolveMediaUrl(url)} alt="Selected" className="aspect-square w-full object-cover" />
              <button
                type="button"
                className="absolute right-1 top-1 rounded-full bg-white/90 p-1"
                data-testid={`${testIdPrefix}-remove-selected`}
                onClick={() => onChange(mode === "multi" ? selectedUrls.filter((item) => item !== url) : "")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="mt-3 max-h-64 overflow-auto rounded-lg border bg-white p-2" data-testid={`${testIdPrefix}-library`}>
          {loading ? (
            <p className="flex items-center gap-2 p-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading assets…</p>
          ) : !assets.length ? (
            <p className="p-3 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
              No images in Assets yet. Upload photos in Dashboard → Assets, then pick them here.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {assets.map((asset) => {
                const selected = selectedUrls.includes(asset.url);
                return (
                  <button
                    key={asset.id}
                    type="button"
                    data-testid={`${testIdPrefix}-asset-${asset.id}`}
                    className={`relative overflow-hidden rounded-lg border ${selected ? "border-[#EA6A3B] ring-2 ring-[#EA6A3B]/40" : "border-transparent"}`}
                    onClick={() => toggle(asset)}
                  >
                    <img src={resolveMediaUrl(asset.url)} alt={asset.original_filename || "Asset"} className="aspect-square w-full object-cover" />
                    {selected && (
                      <span className="absolute right-1 top-1 rounded-full bg-[#EA6A3B] p-1 text-white">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
